import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useZoom } from "../../Context/Zoom"; // <-- import hook
import rsilogo from '../Assets/logo.svg'
import seglogo from '../Assets/seg-logo.svg'
import profile from '../Assets/profile.svg'
import config from '../../Context/Config'
import { refreshToken } from "../../Context/RefreshToken";
import HelpForm from "./help_form";

function Header({ pageTitle }) {
    const navigate = useNavigate();
    const location = useLocation();
    const path = location.pathname;

    const [logoSrc, setLogoSrc] = React.useState("");
    const [showHelpPopup, setShowHelpPopup] = React.useState(false);
    const newDept = localStorage.getItem("digital-form-dept-type");

    const practice = localStorage.getItem("digital-form-dept-practice");

    const showNav = ['/daily-appointments', '/queue', '/help', '/admin', '/images'].includes(path);
    const showToggle = path === '/daily-appointments';

    const { zoom, setZoom, resetZoom } = useZoom(); // <-- use zoom here

    React.useEffect(() => {
        const storedLogo = localStorage.getItem("digital-form-dept-practice");
        setLogoSrc(storedLogo === "rsi" ? rsilogo : seglogo);
    }, []);

    const handleLogout = async () => {
        if (localStorage.getItem("digital-form-locked") === "true") {
            return;
        }

        try {
            const refreshToken = localStorage.getItem(
                "digital-form-refresh_token"
            );

            await fetch(`${config.API_BASE_URL}/auth/logout`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `${localStorage.getItem("digital-form-idtoken")}`,
                    "action_id": config.ACTIONID["edit"],
                    "practice": localStorage.getItem("digital-form-dept-practice")
                },
                body: JSON.stringify({
                    refresh_token: refreshToken
                }),
            });

        } catch (error) {
            console.error("Logout failed", error);
        } finally {

            // Always clear frontend storage
            const keysToRemove = [
                "digital-form-accesstoken",
                "digital-form-idtoken",
                "digital-form-expiry",
                "digital-form-user_details",
                "digital-form-refresh_token",
                "digital-form-login_response",
                "digital-form-dept-id",
                "digital-form-dept-name",
                "digital-form-dept-practice",
                "digital-form-dept-type",
                "selectedLocation",
                "defaultLocation"
            ];

            keysToRemove.forEach((key) =>
                localStorage.removeItem(key)
            );

            navigate("/");
        }
    };


    const handleDeptChange = (dept) => {
        if (localStorage.getItem("digital-form-locked") !== 'true') {
            localStorage.setItem("digital-form-dept-type", dept);
            window.location.reload();
        }
    };

    const handleTogglePractice = async () => {
        if (localStorage.getItem("digital-form-locked") !== 'true') {
            const response = await fetch(`${config.API_BASE_URL}/practice_change`, {
                method: "POST",
                headers: {
                    "action_id": config.ACTIONID['edit'], // Assuming this is set correctly
                    "Content-Type": "application/json",
                    Authorization: `${localStorage.getItem("digital-form-idtoken")}`,
                    "practice": practice

                }
            });
            if (!response.ok) {
                console.error("Failed to update practice");
                return;
            }
            const pracInfo = await response.json();
            localStorage.setItem("digital-form-dept-practice", pracInfo.practice);
            localStorage.setItem("digital-form-dept-id", pracInfo.dept_id);
            await refreshToken({
                navigate,
                onSuccess: (token) => {
                    if (token?.access_token && token?.refresh_token) {
                        const newExpiry = Date.now() + token.expires_in * 1000;
                        localStorage.setItem("digital-form-accesstoken", token.access_token);
                        localStorage.setItem("digital-form-idtoken", token.id_token);
                        localStorage.setItem("digital-form-refresh_token", token.refresh_token);
                        localStorage.setItem("digital-form-expiry", newExpiry);
                    }
                },
                onError: (err) => console.error("Failed to refresh token", err),
                autoReload: false,
            });
            window.location.reload();
        }
    };

    return (
        <header className="bg-white shadow-sm px-6 py-3 flex flex-col md:flex-row flex-wrap items-center justify-between h-auto gap-6">
            {/* Row 1: Logo and Title */}
            <div className="flex items-center gap-6 w-full md:w-auto">
                <img
                    src={logoSrc}
                    alt="Logo"
                    className="w-20 h-25 pointer-events-none bg-blue p-2 rounded"
                />   
                <h1 className="text-xl font-semibold text-gray-800 font-['Roboto']">
                    {pageTitle}
                </h1>
            </div>

            {/* Row 2: Controls (Zoom and Toggles) */}
            <div className="flex flex-wrap items-center gap-6 w-full md:w-auto">
                {/* Zoom Control */}
                <div className="flex items-center gap-4 bg-gray-100 rounded-lg px-4 py-2">
                    <button
                        onClick={() => setZoom((z) => Math.max(z - 0.1, 0.5))}
                        className="bg-red-100 text-red-600 rounded px-3 py-2 text-sm font-semibold hover:bg-red-200 transition-colors"
                    >
                        A-
                    </button>
                    <span className="text-sm font-medium text-gray-600 px-2">
                        {Math.round(zoom * 100)}%
                    </span>
                    <button
                        onClick={() => setZoom((z) => Math.min(z + 0.1, 2))}
                        className="bg-green-100 text-green-600 rounded px-3 py-2 text-sm font-semibold hover:bg-green-200 transition-colors"
                    >
                        A+
                    </button>
                    <button
                        onClick={resetZoom}
                        className="bg-gray-200 text-gray-700 rounded px-3 py-2 text-sm font-semibold hover:bg-gray-300 transition-colors ml-1"
                    >
                        Reset Zoom
                    </button>
                </div>

                {/* Department Type Selector */}
                {showToggle && (
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-600">Type:</span>
                        <div className="flex gap-0 bg-gray-200 rounded-lg p-1">
                            <button
                                onClick={() => handleDeptChange("fdtm")}
                                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                                    newDept === 'fdtm'
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-transparent text-gray-600 hover:text-gray-800'
                                }`}
                            >
                                Intake
                            </button>
                            <button
                                onClick={() => handleDeptChange("surgical")}
                                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                                    newDept === 'surgical'
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-transparent text-gray-600 hover:text-gray-800'
                                }`}
                            >
                                Surgical
                            </button>
                            <button
                                onClick={() => handleDeptChange("exam")}
                                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                                    newDept === 'exam'
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-transparent text-gray-600 hover:text-gray-800'
                                }`}
                            >
                                Exam
                            </button>
                        </div>
                    </div>
                )}

                {/* Practice Toggle */}
                {showToggle && (
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-600">RSI</span>
                        <div
                            onClick={handleTogglePractice}
                            className={`relative w-14 h-7 rounded-full cursor-pointer transition-colors duration-300 ${practice === 'seg' ? 'bg-blue-500' : 'bg-gray-300'
                                }`}
                        >
                            <span
                                className={`absolute left-0 top-0 w-7 h-7 bg-white rounded-full shadow transform transition-transform duration-300 ${practice === 'seg' ? 'translate-x-7' : 'translate-x-0'
                                    }`}
                            />
                        </div>
                        <span className="text-sm font-medium text-gray-600">SEG</span>
                    </div>
                )}
            </div>

            {/* Row 3: Profile Section */}
            <div className="flex items-center gap-5 w-full md:w-auto md:ml-auto">
                <button
                    onClick={() => setShowHelpPopup(true)}
                    type="button"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 focus:outline-none text-base font-medium transition-colors"
                    aria-label="Open Help Form"
                >
                    <i className="fas fa-circle-question" aria-hidden="true" />
                    Help
                </button>

                <div className="flex items-center gap-4">
                    <div className="text-right">
                        <span className="text-gray-700 font-medium text-base block">{config.USERNAME}</span>
                    </div>

                    <div className="relative group">
                        <img
                            src={profile}
                            alt="Profile"
                            className="w-11 h-11 rounded-full border-2 border-gray-300 object-cover shadow transition-transform duration-200 hover:scale-105"
                        />
                    </div>

                    {showNav && (
                        <button
                            onClick={handleLogout}
                            type="button"
                            className="flex items-center gap-1 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 focus:outline-none text-base font-medium transition-colors"
                            aria-label="Logout"
                        >
                            <i className="fas fa-sign-out-alt" aria-hidden="true" />
                        </button>
                    )}
                </div>
            </div>
            {/* Second Row: Navigation */}
            {/* Second Row: Navigation */}
            {showNav && (
                <nav className="flex flex-wrap gap-6 text-lg font-medium text-gray-600 w-full mt-2 justify-center md:justify-start">
                    <a
                        href="/daily-appointments"
                        className="hover:text-blue-600 transition-colors flex items-center gap-2"
                    >
                        <i className="fas fa-home"></i>
                        <span>Dashboard</span>
                    </a>

                    <a
                        href="/help"
                        className="hover:text-blue-600 transition-colors flex items-center gap-2"
                    >
                        <i className="fas fa-book-open"></i>
                        <span>App Guide</span>
                    </a>

                    <a
                        href="/admin"
                        className="hover:text-blue-600 transition-colors flex items-center gap-2"
                    >
                        <i className="fas fa-cog"></i>
                        <span>Admin</span>
                    </a>
                </nav>
            )}
            {showHelpPopup && <HelpForm onClose={() => setShowHelpPopup(false)} />}
        </header>


    );
}

export default Header;
